import { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useGetDepartmentsQuery } from '../../Redux/Service/Department';
import { useGetRolesQuery } from '../../Redux/Service/RoleApi';
import { Link, useNavigate } from 'react-router-dom';
import { useCreateUserMutation } from '../../Redux/Service/UserApi';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StyledDashboardLayout from '../style';
import { Grid, FormHelperText } from '@mui/material';

function UserAdd() {
   const [departments, setDepartment] = useState(null);
   const [fileError, setFileError] = useState(null);
   const [roles, setRoles] = useState(null);
   const [signaturePreviews, setSignaturePreviews] = useState({});
   const navigate = useNavigate();
   const {
      data: departmentsData,
      isLoading: isLoadingForDepartment,
      isError: isErrorForDepartment,
      error: errorForDepartment,
   } = useGetDepartmentsQuery();
   const {
      data: rolesData,
      isLoading: isLoadingForRole,
      isError: isErrorForRole,
      error: errorForRole,
   } = useGetRolesQuery();
   const [createUser] = useCreateUserMutation();

   useEffect(() => {
      if (!isLoadingForDepartment && !isErrorForDepartment) {
         setDepartment(departmentsData?.response);
         console.log(departmentsData?.response);
      }
      if (!isLoadingForRole && !isErrorForRole) {
         setRoles(rolesData?.response);
         console.log(rolesData?.response);
      }
   }, [
      isLoadingForRole,
      isErrorForRole,
      departments,
      isLoadingForDepartment,
      isErrorForDepartment,
      roles,
      departmentsData,
      rolesData,
   ]);

   const initialValues = {
      name: '',
      email: '',
      phoneNo: '',
      roleId: '',
      departmentId: '',
      password: '',
      signature: '',
   };
   const emailRegex =
      /^(?!.*\.\.)(?!.*@@)[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;

   const validationSchema = Yup.object({
      name: Yup.string()
         .matches(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces')
         .matches(/^[a-zA-Z]/, 'Name cannot start with a space')
         .max(50, "Name should be atmost 50 characters")
         .min(2, 'Name must be at least 2 characters')
         .required('Name is required'),

      email: Yup.string()
         .test(
            "valid-email",
            "Please enter a valid email address",
            (value) => {
               if (!value) return true;

               // Additional checks
               if (value.startsWith(".")) return false;
               if (value.endsWith(".")) return false;
               if (value.includes("..")) return false;

               return emailRegex.test(value);
            }
         )
         .max(254, "Email should be atmost 254 characters")
         .required('Email is required'),
      phoneNo: Yup.string()
         .matches(/^$|^[0-9]+$/, 'Phone number can only contain digits')
         .min(10, 'Phone number must be at least 10 digits')
         .max(15, 'Phone number cannot exceed 15 digits')
         .notRequired(),
      roleId: Yup.string().required('Role is required'),
      departmentId: Yup.string().required('Department is required'),
      // password: Yup.string()
      //    .min(6, 'Password must be at least 6 characters')
      //    .required('Password is required'),
      signature: Yup.mixed().nullable().optional(),
   });

   const handleNameInput = (e, setFieldValue) => {
      const value = e.target.value;

      if (value.length === 1 && value === ' ') {
         return;
      }

      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
      const cleanedValue = filteredValue.replace(/\s+/g, ' ');

      setFieldValue('name', cleanedValue);
   };

   const handlePhoneInput = (e, setFieldValue) => {
      const value = e.target.value;
      const filteredValue = value.replace(/[^0-9]/g, '');
      setFieldValue('phoneNo', filteredValue);
   };

   const handleSignatureUpload = (event, setFieldValue) => {
      const file = event.currentTarget.files[0];

      // if (!file) {
      //    toast.error('No file selected');
      //    setFileError("Signature is required");
      //    return;
      // };

      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const maxFileSize = 2 * 1024 * 1024; // 2MB

      if (!validImageTypes.includes(file.type)) {
         toast.error('Only JPEG, JPG, and PNG files are allowed for signature');
         setFileError("Only JPEG, JPG, and PNG files are allowed for signature");
         event.currentTarget.value = '';
         return;
      }

      if (file.size > maxFileSize) {
         toast.error('Signature file size must not exceed 2MB');
         setFileError("File too big, should be within 2MB");
         event.currentTarget.value = '';
         return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
         setSignaturePreviews((prev) => ({
            ...prev,
            signature: e.target.result,
         }));
      };
      reader.readAsDataURL(file);
      setFieldValue('signature', file);
      setFileError('');
   };

   const handleRemoveSignature = (setFieldValue) => {
      setSignaturePreviews((prev) => ({
         ...prev,
         signature: null,
      }));
      setFieldValue('signature', null);
      const signatureInput = document.getElementById('signature');
      if (signatureInput) {
         signatureInput.value = '';
      }
   };

   const handleSubmit = async (values, { resetForm, setSubmitting }) => {
      try {
         setSubmitting(true);
         values.roleId = parseInt(values.roleId);
         values.departmentId = parseInt(values.departmentId);

         const formData = new FormData();
         formData.append('name', values.name.trim());
         formData.append('email', values.email.trim().toLowerCase());
         formData.append('phoneNo', values.phoneNo.trim());
         formData.append('roleId', values.roleId);
         formData.append('departmentId', values.departmentId);
         // formData.append('password', values.password);

         if (values.signature && values.signature instanceof File) {
            formData.append('signature', values.signature);
         }

         const createUserData = await createUser(formData);
         console.log(createUserData);
         if (createUserData && createUserData?.error) {
            toast.error(createUserData?.error?.data?.message);
         } else if (createUserData) {
            toast.success(createUserData.data?.message);
            navigate('/user');
         } else {
            resetForm();
            toast.error('Something went wrong, Please try again later!');
         }
      } catch (err) {
         console.error("err")
         toast.error('Something went wrong, Please try again later!');
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between">
                     <div className="pageTitle">
                        <h1 className="m-0">Add User</h1>
                     </div>
                     <div className="btnWrap">
                        <Link to="/user" className="btn addBtn">
                           {' '}
                           <ArrowBackIcon /> Back
                        </Link>
                     </div>
                  </div>
               </div>
            </div>
            <div className="card customCard p-3 m-3">
               <div className="card-body">
                  {isLoadingForRole || isLoadingForDepartment ? (
                     <div>Loading...</div>
                  ) : errorForRole || errorForDepartment ? (
                     <div>
                        Error: {errorForRole?.message}
                        {errorForDepartment?.message}
                     </div>
                  ) : (
                     <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                     >
                        {({ setFieldValue, values, touched, errors, isSubmitting }) => (
                           <Form autoComplete="off">
                              <input
                                 type="text"
                                 name="fakeusernameremembered"
                                 style={{ display: 'none' }}
                              />
                              <input
                                 type="password"
                                 name="fakepasswordremembered"
                                 style={{ display: 'none' }}
                              />
                              <div className="form-group">
                                 <label htmlFor="name">Name *</label>
                                 <Field
                                    type="text"
                                    name="name"
                                    autoComplete="off"
                                    className="form-control"
                                    value={values.name}
                                    onChange={(e) => handleNameInput(e, setFieldValue)}
                                    placeholder="Enter full name (letters only, cannot start with space)"
                                    maxLength={50}
                                    disabled={isSubmitting}
                                    onError={touched.name && errors.name}
                                 />
                                 {/* <ErrorMessage
                                    name="name"
                                    component="div"
                                    className="text-danger"
                                 /> */}
                                 <small className="form-text text-muted">
                                    Only letters and spaces are allowed. Cannot start with a space (Max 50  characters & Min 2 characters).
                                 </small>
                                 {touched.name && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                    {touched.name && errors.name} </FormHelperText>)}
                              </div>
                              <Grid container spacing={4}>
                                 <Grid item md={6} xs={12}>
                                    <div className="form-group">
                                       <label htmlFor="role">Role *</label>
                                       <Field
                                          as="select"
                                          name="roleId"
                                          className="form-control"
                                          disabled={isSubmitting}
                                          onError={touched.roleId && errors.roleId}
                                       >
                                          <option value="">Select Role</option>
                                          {roles?.map((val) => (
                                             <option key={val.id} value={val.id}>
                                                {val.name}
                                             </option>
                                          ))}
                                       </Field>
                                       {/* <ErrorMessage
                                          name="roleId"
                                          component="div"
                                          className="text-danger"
                                       /> */}
                                       {touched.roleId && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                          {touched.roleId && errors.roleId} </FormHelperText>)}
                                    </div>
                                 </Grid>
                                 <Grid item md={6} xs={12}>
                                    <div className="form-group">
                                       <label htmlFor="department">Department *</label>
                                       <Field
                                          as="select"
                                          name="departmentId"
                                          className="form-control"
                                          disabled={isSubmitting}
                                          onError={touched.departmentId && errors.departmentId}
                                       >
                                          <option value="">Select Department</option>
                                          {departments?.map((val) => (
                                             <option key={val.id} value={val.id}>
                                                {val.name}
                                             </option>
                                          ))}
                                       </Field>
                                       {/* <ErrorMessage
                                          name="departmentId"
                                          component="div"
                                          className="text-danger"
                                       /> */}
                                       {touched.departmentId && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                          {touched.departmentId && errors.departmentId} </FormHelperText>)}
                                    </div>
                                 </Grid>
                                 <Grid item md={6} xs={12}>
                                    <div className="form-group">
                                       <label htmlFor="email">Email Address *</label>
                                       <Field
                                          type="email"
                                          name="email"
                                          autoComplete="off"
                                          className="form-control"
                                          placeholder="Enter valid email address"
                                          disabled={isSubmitting}
                                          onError={touched.email && errors.email}
                                       />
                                       {/* <ErrorMessage
                                          name="email"
                                          component="div"
                                          className="text-danger"
                                       /> */}
                                       {touched.email && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                          {touched.email && errors.email} </FormHelperText>)}
                                    </div>
                                 </Grid>
                                 <Grid item md={6} xs={12}>
                                    <div className="form-group">
                                       <label htmlFor="phone">Phone Number</label>
                                       <Field
                                          type="text"
                                          name="phoneNo"
                                          autoComplete="off"
                                          className="form-control"
                                          value={values.phoneNo}
                                          onChange={(e) => handlePhoneInput(e, setFieldValue)}
                                          placeholder="Enter phone number (digits only, optional)"
                                          maxLength={15}
                                          disabled={isSubmitting}
                                          onError={touched.phoneNo && errors.phoneNo}
                                       />
                                       {/* <ErrorMessage
                                          name="phoneNo"
                                          component="div"
                                          className="text-danger"
                                       /> */}
                                       <small className="form-text text-muted">
                                          Only numbers are allowed (10-15 digits, optional)
                                       </small>
                                       {touched.phoneNo && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                          {touched.phoneNo && errors.phoneNo} </FormHelperText>)}
                                    </div>
                                 </Grid>
                              </Grid>
                              {/* <div className="form-group">
                                 <label htmlFor="password">Password *</label>
                                 <Field
                                    type="password"
                                    name="password"
                                    autoComplete="new-password"
                                    className="form-control"
                                    placeholder="Enter password (minimum 6 characters)"
                                    disabled={isSubmitting}
                                 />
                                 <ErrorMessage
                                    name="password"
                                    component="div"
                                    className="text-danger"
                                 />
                              </div> */}

                              <div className="form-group">
                                 <label htmlFor="signature">Signature (Optional)</label>
                                 <div className="mb-3">
                                    {signaturePreviews.signature && (
                                       <div className="mb-3">
                                          <img
                                             src={signaturePreviews.signature}
                                             alt="Signature Preview"
                                             style={{
                                                maxHeight: '150px',
                                                maxWidth: '300px',
                                                border: '1px solid #ddd',
                                                padding: '5px',
                                                borderRadius: '4px',
                                             }}
                                          />
                                          <div className="mt-2">
                                             <button
                                                type="button"
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleRemoveSignature(setFieldValue)}
                                             >
                                                Remove Signature
                                             </button>
                                          </div>
                                       </div>
                                    )}
                                 </div>
                                 <input
                                    id="signature"
                                    type="file"
                                    name="signature"
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={(e) => handleSignatureUpload(e, setFieldValue)}
                                    className="form-control"
                                    disabled={isSubmitting}
                                 />
                                 <small className="form-text text-muted">
                                    Allowed formats: JPEG, JPG, PNG. Maximum file size: 2MB
                                 </small>
                                 {fileError && (<FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                    {fileError} </FormHelperText>)}
                                 {!fileError && touched.signature && errors.signature && (
                                    <FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px' }}>
                                       {errors.signature}
                                    </FormHelperText>
                                 )}
                              </div>

                              <button
                                 type="submit"
                                 className="btn commonBtn"
                                 disabled={isSubmitting}
                              >
                                 {isSubmitting ? (
                                    <>
                                       <span
                                          className="spinner-border spinner-border-sm mr-2"
                                          role="status"
                                          aria-hidden="true"
                                       ></span>
                                       Submitting...
                                    </>
                                 ) : (
                                    'Submit'
                                 )}
                              </button>
                           </Form>
                        )}
                     </Formik>
                  )}
               </div>
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default UserAdd;
